import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { UsersService } from '../users.service';
import { UserFormData, FieldValidationState, Supervisor, SupervisoryLevel, GeoNode } from '../users.model';
import { GeographicNodesService } from '../../geographic-nodes/geographic-nodes.service';
import { GeographicNode } from '../../geographic-nodes/geographic-node.model';
import { FieldDiff, computeUserDiff } from '../../../shared/utils/user-diff.util';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { HasUnsavedChanges } from '../../../shared/guards/unsaved-changes.guard';

interface SaveSuccessSummary {
  totalChanged: number;
  changes: SavedChangeItem[];
}

interface SavedChangeItem {
  type: 'role' | 'supervisor' | 'geo' | 'phone' | 'email' | 'name' | 'access';
  label: string;
  summary: string;
}

@Component({
  selector: 'app-edit-user',
  standalone: false,
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss'
})
export class EditUserComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private usersService = inject(UsersService);
  private geoNodesService = inject(GeographicNodesService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private _subs: Subscription[] = [];
  private geoSearchSubject = new Subject<string>();

  readonly cancelAction = new EventEmitter<void>();
  readonly saveAction = new EventEmitter<void>();

  userId = '';
  loading = true;
  saving = false;
  submitted = false;
  saveError: { type: string; message: string; code?: string } | null = null;

  originalData: UserFormData = this.emptyForm();
  formData: UserFormData = this.emptyForm();

  emailState: FieldValidationState = { status: 'idle' };
  phoneState: FieldValidationState = { status: 'idle' };

  fullNameError = false;
  supervisoryLevelError = false;
  roleError = false;
  supervisorError = false;

  showSupervisorModal = false;
  showSupervisoryLevelRequiredError = false;

  geoNodes: GeoNode[] = [];
  geoLoading = false;

  saveSuccess = false;
  saveSuccessSummary: SaveSuccessSummary | null = null;
  countdownDisplay = 10;
  private countdownInterval: any;

  ROLE_PERMISSIONS: Record<SupervisoryLevel, string[]> = {
    L3: ['Manage own region agents', 'Dispatch & operations', 'Region reports'],
    L2: ['Manage own region agents', 'Dispatch & operations', 'Region reports'],
    L1: ['Execute assigned tasks']
  };

  private emptyForm(): UserFormData {
    return {
      fullName: '',
      email: '',
      phone: '',
      role: null,
      PlatformAccess: 'BOTH',
      supervisoryLevel: null,
      supervisorId: null,
      supervisor: null,
      geographicNodeId: null,
      geographicNodeLabel: null,
      geographicNodeIds: []
    };
  }

  get diff(): FieldDiff[] {
    if (this.loading) return [];
    return computeUserDiff(this.originalData, this.formData);
  }

  get headerActions(): HeaderAction[] {
    const d = this.diff;
    const saveBtn: HeaderAction = this.saveSuccess
      ? { label: '✓ Saved', variant: 'primary', disabled: true }
      : {
          label: d.length > 0 ? `Save ${d.length} change${d.length > 1 ? 's' : ''}` : 'Save',
          variant: 'primary',
          disabled: d.length === 0 || this.saving,
          emitter: this.saveAction
        };
    return [
      { label: 'Cancel', variant: 'secondary', disabled: this.saving, emitter: this.cancelAction },
      saveBtn
    ];
  }

  hasUnsavedChanges(): boolean {
    if (this.saveSuccess) return false;
    return this.diff.length > 0;
  }

  isSectionModified(section: 'identity' | 'role-access' | 'hierarchy'): boolean {
    const d = this.diff;
    switch (section) {
      case 'identity':   return d.some(f => ['Full name', 'Email', 'Phone'].includes(f.field));
      case 'role-access': return d.some(f => ['Supervisory Level', 'Role', 'Access Type'].includes(f.field));
      case 'hierarchy':  return d.some(f => ['Supervisor', 'Territory'].includes(f.field));
    }
  }

  isFieldModified(field: string): boolean {
    return this.diff.some(d => d.field === field);
  }

  getFieldDiff(field: string): FieldDiff | undefined {
    return this.diff.find(d => d.field === field);
  }

  getSupervisoryLevelLabel(level: SupervisoryLevel | null): string {
    if (!level) return '—';
    const map: Record<SupervisoryLevel, string> = {
      L1: 'Field Agent',
      L2: 'Supervisor',
      L3: 'Regional Manager'
    };
    return map[level];
  }

  ngOnInit(): void {
    this._subs.push(
      this.cancelAction.subscribe(() => this.onCancel()),
      this.saveAction.subscribe(() => this.onSave()),
    );

    this.userId = this.route.snapshot.paramMap.get('id') ?? '';

    this.loadGeoNodes();

    this._subs.push(
      this.geoSearchSubject
        .pipe(
          debounceTime(400),
          switchMap((query) => {
            this.geoLoading = true;
            return this.geoNodesService.findAll({ search: query || undefined, limit: 20 });
          })
        )
        .subscribe({
          next: (res) => {
            this.geoNodes = this.toGeoNodes(res.data);
            this.geoLoading = false;
          },
          error: () => { this.geoLoading = false; },
        })
    );

    this.usersService.getUserById(this.userId).subscribe(user => {
      
      this.originalData = { ...user };
      this.formData = { ...user };
      this.emailState = { status: 'valid' };
      this.phoneState = { status: 'valid' };
      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this.clearCountdown();
  }

  private loadGeoNodes(): void {
    this.geoLoading = true;
    this.geoNodesService.findAll({ limit: 500, status: 'active' }).subscribe({
      next: (res) => {
        this.geoNodes = this.buildTree(res.data);
        this.geoLoading = false;
      },
      error: () => { this.geoLoading = false; },
    });
  }

  private buildTree(nodes: GeographicNode[]): GeoNode[] {
    const map = new Map<string, GeoNode>();
    for (const n of nodes) {
      map.set(n.id, { id: n.id, label: n.name, code: n.code, userCount: n.activePosCount ?? 0, children: [], expanded: true });
    }
    const roots: GeoNode[] = [];
    for (const n of nodes) {
      const node = map.get(n.id)!;
      if (n.parent_id && map.has(n.parent_id)) {
        map.get(n.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    }
    for (const node of map.values()) {
      if (!node.children?.length) delete node.children;
    }
    return roots;
  }

  private toGeoNodes(nodes: GeographicNode[]): GeoNode[] {
    return nodes.map(n => ({ id: n.id, label: n.name, code: n.code, userCount: n.activePosCount ?? 0 }));
  }

  onGeoSearch(query: string): void {
    this.geoSearchSubject.next(query);
  }

  isFormValid(): boolean {
    this.fullNameError = !this.formData.fullName.trim();
    this.supervisoryLevelError = !this.formData.supervisoryLevel;
    this.roleError = !this.formData.role;
    this.supervisorError = this.formData.supervisoryLevel !== 'L3' && !this.formData.supervisor;
    return (
      !this.fullNameError &&
      !this.supervisoryLevelError &&
      !this.roleError &&
      !this.supervisorError &&
      this.emailState.status === 'valid' &&
      this.phoneState.status === 'valid'
    );
  }

  onSave(): void {
    this.submitted = true;
    this.saveError = null;

    const pendingDiff = this.diff;
    if (pendingDiff.length === 0) return;
    if (!this.isFormValid()) return;

    this.saving = true;
    this.usersService.updateUser(this.userId, this.formData).subscribe({
      next: (updated) => {
        this.saving = false;
        this.originalData = { ...updated };
        this.formData = { ...updated };
        this.emailState = { status: 'valid' };
        this.phoneState = { status: 'valid' };
        this.submitted = false;
        this.saveSuccessSummary = this.buildSuccessSummary(pendingDiff);
        this.saveSuccess = true;
        this.startRedirectCountdown();
      },
      error: (err) => {
        this.saving = false;
        this.handleSaveError(err);
      }
    });
  }

  private buildSuccessSummary(diff: FieldDiff[]): SaveSuccessSummary {
    const changes: SavedChangeItem[] = diff.map(d => {
      switch (d.field) {
        case 'Role':           return { type: 'role',       label: 'role',       summary: `${d.oldValue} → ${d.newValue}` };
        case 'Supervisor':     return { type: 'supervisor', label: 'supervisor', summary: `${d.oldValue} → ${d.newValue}` };
        case 'Territory':      return { type: 'geo',        label: 'geo',        summary: d.newValue };
        case 'Phone':          return { type: 'phone',      label: 'phone',      summary: `${d.oldValue} → ${d.newValue}` };
        case 'Email':          return { type: 'email',      label: 'email',      summary: `${d.oldValue} → ${d.newValue}` };
        case 'Full name':      return { type: 'name',       label: 'name',       summary: `${d.oldValue} → ${d.newValue}` };
        default:               return { type: 'access',     label: d.field.toLowerCase(), summary: `${d.oldValue} → ${d.newValue}` };
      }
    });
    return { totalChanged: diff.length, changes };
  }

  private startRedirectCountdown(): void {
    this.countdownDisplay = 10;
    this.countdownInterval = setInterval(() => {
      this.countdownDisplay--;
      if (this.countdownDisplay <= 0) {
        this.clearCountdown();
        this.router.navigate(['/users']);
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  onDone(): void {
    this.clearCountdown();
    this.router.navigate(['/users']);
  }

  onViewActivityLog(): void {
    this.clearCountdown();
    this.router.navigate(['/users', this.userId, 'activity']);
  }

  onDiscardAll(): void {
    this.formData = { ...this.originalData };
    this.emailState = { status: 'valid' };
    this.phoneState = { status: 'valid' };
    this.submitted = false;
    this.saveError = null;
  }

  onLevelChange(level: SupervisoryLevel): void {
    this.formData.supervisoryLevel = level;
    this.formData.supervisor = null;
    this.formData.supervisorId = null;
    this.supervisoryLevelError = false;
    this.showSupervisoryLevelRequiredError = false;
  }

  onRoleChange(role: { id: string; label: string } | null): void {
    this.formData.role = role ? { id: role.id, name: role.label } : null;
    this.roleError = false;
  }

  onPickSupervisor(): void {
    if (!this.formData.supervisoryLevel) {
      this.showSupervisoryLevelRequiredError = true;
      return;
    }
    this.showSupervisoryLevelRequiredError = false;
    this.showSupervisorModal = true;
  }

  closeSupervisorModal(): void {
    this.showSupervisorModal = false;
  }

  onSupervisorSelected(supervisor: Supervisor): void {
    this.formData.supervisor = supervisor;
    this.formData.supervisorId = supervisor.id;
    this.supervisorError = false;
    this.showSupervisorModal = false;
  }

  onGeoSelectionChange(ids: string[]): void {
    this.formData.geographicNodeIds = [...ids];
  }

  onCancel(): void {
    this.router.navigate(['/users']);
  }

  handleSaveError(err: any): void {
    if (err.status === 409) {
      const code = err.error?.code;
      if (code === 'CONFLICT_PHONE_DUPLICATE') {
        this.saveError = { type: 'phone_conflict', code, message: `Phone ${this.formData.phone} is already in use.` };
        this.phoneState = { status: 'conflict', message: this.saveError.message };
      } else if (code === 'CONFLICT_EMAIL_DUPLICATE') {
        this.saveError = { type: 'email_conflict', code, message: `Email ${this.formData.email} is already in use.` };
        this.emailState = { status: 'conflict', message: this.saveError.message };
      } else {
        this.saveError = { type: 'generic', message: 'A conflict was detected at save time.' };
      }
    } else {
      this.saveError = { type: 'generic', message: err.message || 'An unexpected error occurred.' };
    }
  }

  onEditPhoneConflict(): void { this.saveError = null; this.phoneState = { status: 'idle' }; }
  onEditEmailConflict(): void { this.saveError = null; this.emailState = { status: 'idle' }; }
  onDiscardConflict(): void { this.router.navigate(['/users']); }
}
